import { framePieces, type SceneWindow } from '../timing/sceneMap'
import type { Range } from '../timing/skipRanges'
import type { TimelinePiece } from '../utils/mediaTimeline'
import type { CaptionSourceItem } from './captionWords'
import { framesToSeconds } from './frames'
import {
  transcriptAssetId,
  type AudioItem,
  type SceneItem,
  type TimelineAsset,
  type VideoItem,
} from './types'

/**
 * A hydrated media item as a scene (#77): its window in recording time, the
 * cuts its recording's transcript asset carries, and the frame pieces it
 * plays. The one reading of `zealous.sourceEnd` for the layers, the
 * visualizer, captions and the editor.
 */

/** A free media item, or a scene (#541), which plays its window the same way. */
type MediaItem = VideoItem | AudioItem | SceneItem

export function itemSourceStart(item: MediaItem): number {
  return item.type === 'video'
    ? item.videoStartFromInSeconds
    : (item.audioStartFromInSeconds ?? 0)
}

/** The item's window; without a recorded end, as long as the item. */
export function itemWindow(item: MediaItem, fps: number): SceneWindow {
  const start = itemSourceStart(item)
  return {
    start,
    end: item.zealous.sourceEnd ?? start + item.durationInFrames / fps,
  }
}

/** The recording an item plays, or null for an asset-sourced one. */
export function itemRecordingId(item: MediaItem): string | null {
  return item.zealous.source?.kind === 'recording'
    ? item.zealous.source.recordingId
    : null
}

/** The cuts the item plays around: none for an asset or an unloaded transcript. */
export function itemSkips(
  item: MediaItem,
  assets: Readonly<Record<string, TimelineAsset>>,
): readonly Range[] {
  const recordingId = itemRecordingId(item)
  if (!recordingId) return []
  const asset = assets[transcriptAssetId(recordingId)]
  return asset?.type === 'transcript' ? (asset.skips ?? []) : []
}

/** The frame pieces the item plays, on its own timeline (`framePieces`). */
export function itemFramePieces(
  item: MediaItem,
  assets: Readonly<Record<string, TimelineAsset>>,
  fps: number,
): TimelinePiece[] {
  return framePieces({
    window: itemWindow(item, fps),
    skips: itemSkips(item, assets),
    durationInFrames: item.durationInFrames,
    fps,
  })
}

/**
 * A bound media item as the caption and speaker mappers read it, with times
 * relative to the follower that reads it (`origin`, seconds).
 */
export function captionSourceOf(
  item: MediaItem,
  assets: Readonly<Record<string, TimelineAsset>>,
  fps: number,
  origin: number,
): CaptionSourceItem {
  const window = itemWindow(item, fps)
  return {
    from: framesToSeconds(item.from, fps) - origin,
    duration: framesToSeconds(item.durationInFrames, fps),
    sourceStart: window.start,
    sourceEnd: window.end,
    skips: itemSkips(item, assets),
    recordingId: itemRecordingId(item),
  }
}
